import { baseApi } from '@/api/core/baseApi';
import { unwrapApiResponse } from '@/api/patterns/crud';
import type {
  PlatformOrganizationsResponse,
  GetPlatformOrgsArgs,
  DisableOrgArgs,
  DeleteOrgArgs,
} from '../types';

export const platformOrganizationsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPlatformOrganizations: builder.query<PlatformOrganizationsResponse, GetPlatformOrgsArgs | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.search?.trim()) params.set('search', args.search.trim());
        params.set('limit', String(args?.limit ?? 1000));
        if (args?.includeDisabled) params.set('includeDisabled', 'true');
        const qs = params.toString();
        return `/platform/organizations${qs ? `?${qs}` : ''}`;
      },
      transformResponse: unwrapApiResponse<PlatformOrganizationsResponse>,
      providesTags: [{ type: 'PlatformOrganizations', id: 'LIST' }],
    }),

    disableOrganization: builder.mutation<unknown, DisableOrgArgs>({
      query: ({ id, confirmation, reason }) => ({
        url: `/platform/organizations/${id}/disable`,
        method: 'POST',
        body: { confirmation, reason: reason ?? 'Disabled via platform admin dashboard' },
      }),
      invalidatesTags: [{ type: 'PlatformOrganizations', id: 'LIST' }],
    }),

    enableOrganization: builder.mutation<unknown, string>({
      query: (id) => ({
        url: `/platform/organizations/${id}/enable`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'PlatformOrganizations', id: 'LIST' }],
    }),

    deletePlatformOrganization: builder.mutation<unknown, DeleteOrgArgs>({
      query: ({ id, confirmation, stepUpToken, stepUpTimestamp }) => ({
        url: `/platform/organizations/${id}`,
        method: 'DELETE',
        body: { confirmation },
        headers: stepUpToken ? { 'X-Step-Up-Token': stepUpToken, ...(stepUpTimestamp && { 'X-Step-Up-Timestamp': stepUpTimestamp }) } : undefined,
      }),
      invalidatesTags: [{ type: 'PlatformOrganizations', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetPlatformOrganizationsQuery,
  useDisableOrganizationMutation,
  useEnableOrganizationMutation,
  useDeletePlatformOrganizationMutation,
} = platformOrganizationsApi;
